import { memo } from 'react';
import { ReassignModal } from '../../container/requestModal/ReassignModal';
import { CreateRequestModal } from '../../container/requestModal/CreateRequestModal';
import { CancelWorkflowModal } from '../../layout/bodyLayout/CancelWorkflowModal';
import { PaymentWorkflowType } from '../../../components/container/forms/types/api/graph.types';
import { WorkflowItem } from '../../../hooks/usePageState';

// ============= Types =============
interface ModalManagerProps {
  // Reassign Modal
  isReassignOpen: boolean;
  onReassignClose: () => void;

  // Cancel Modal
  isCancelModalOpen: boolean;
  onCancelModalClose: () => void;
  onCancelWorkflow: () => void;
  isCancelling: boolean;

  // Create Request Modal
  isModalOpen: boolean;
  onModalClose: () => void;
  onCreateRequest: (
    selectedWorkflowType?: PaymentWorkflowType
  ) => Promise<void>;

  // Client/Matter data
  clientData: string[];
  matterData: string[];
  selectedClientLabel: string;
  onClientSearch: (query: string) => void;
  onMatterSearch: (query: string) => void;
  onClientSelect: (option: string) => void;
  onMatterSelect: (option: string) => void;
  isLoadingClients: boolean;
  isLoadingMatters: boolean;

  // Workflow configuration
  showWorkflowTypeSelect: boolean;
  currentWorkflow: WorkflowItem | null | undefined;
  allowedWorkflowTypes: PaymentWorkflowType[];
  isDashboardMode: boolean;
  onOfficeChange?: (office: string) => void;
}

// ============= Component =============
/**
 * ModalManager Component
 * Manages all modals in the BodyLayout
 */
export const ModalManager = memo<ModalManagerProps>(
  ({
    // Reassign Modal
    isReassignOpen,
    onReassignClose,

    // Cancel Modal
    isCancelModalOpen,
    onCancelModalClose,
    onCancelWorkflow,
    isCancelling,

    // Create Request Modal
    isModalOpen,
    onModalClose,
    onCreateRequest,
    clientData,
    matterData,
    selectedClientLabel,
    onClientSearch,
    onMatterSearch,
    onClientSelect,
    onMatterSelect,
    isLoadingClients,
    isLoadingMatters,
    showWorkflowTypeSelect,
    currentWorkflow,
    allowedWorkflowTypes,
    isDashboardMode,
    onOfficeChange,
  }) => {
    return (
      <>
        <ReassignModal
          isOpen={isReassignOpen}
          setIsOpen={() => {}} // Controlled by parent
          onClose={onReassignClose}
        />

        <CancelWorkflowModal
          isOpen={isCancelModalOpen}
          onClose={onCancelModalClose}
          onConfirm={onCancelWorkflow}
          isCancelling={isCancelling}
        />

        <CreateRequestModal
          isOpen={isModalOpen}
          setIsOpen={() => {}} // Controlled by parent
          onClose={onModalClose}
          clientData={clientData}
          matterData={matterData}
          selectedClientLabel={selectedClientLabel}
          onClientSearch={onClientSearch}
          onMatterSearch={onMatterSearch}
          onClientSelect={onClientSelect}
          onMatterSelect={onMatterSelect}
          onCreateRequest={onCreateRequest}
          isLoadingClients={isLoadingClients}
          isLoadingMatters={isLoadingMatters}
          showWorkflowTypeSelect={showWorkflowTypeSelect}
          currentWorkflow={currentWorkflow}
          allowedWorkflowTypes={allowedWorkflowTypes}
          isDashboardMode={isDashboardMode}
          onOfficeChange={onOfficeChange}
        />
      </>
    );
  }
);

ModalManager.displayName = 'ModalManager';
